type PointerState = { isPointerDown: boolean; isPointerUp: boolean };

let initialized = false;
let controller: AbortController | null = null;
let handlePointerDownRef: ((e: PointerEvent) => void) | null = null;
let handlePointerUpRef: ((e: PointerEvent) => void) | null = null;

let state: PointerState = { isPointerDown: false, isPointerUp: true };
const subscribers = new Set<(s: PointerState) => void>();

function notify() {
  subscribers.forEach((cb) => cb(state));
}

function initListeners() {
  if (initialized) {
    return;
  }
  initialized = true;

  const handlePointerUp = () => {
    state = { isPointerDown: false, isPointerUp: true };
    notify();
  };

  const handlePointerDown = () => {
    state = { isPointerDown: true, isPointerUp: false };
    notify();
  };

  handlePointerDownRef = handlePointerDown;
  handlePointerUpRef = handlePointerUp;

  try {
    controller = new AbortController();
    document.addEventListener("pointerdown", handlePointerDown, { signal: controller.signal });
    document.addEventListener("pointerup", handlePointerUp, { signal: controller.signal });
  } catch {
    // Fallback for environments without AbortController support
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("pointerup", handlePointerUp);
  }
}

function cleanupListeners() {
  if (!initialized) {
    return;
  }

  if (controller) {
    controller.abort();
    controller = null;
  } else {
    if (handlePointerDownRef) {
      document.removeEventListener("pointerdown", handlePointerDownRef);
    }
    if (handlePointerUpRef) {
      document.removeEventListener("pointerup", handlePointerUpRef);
    }
  }

  handlePointerDownRef = null;
  handlePointerUpRef = null;
  initialized = false;
}

export function getPointerState(): PointerState {
  return state;
}

export function subscribePointerInteractions(callback: (s: PointerState) => void) {
  if (subscribers.size === 0) {
    initListeners();
  }
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
    if (subscribers.size === 0) {
      cleanupListeners();
    }
  };
}
